import React, { useState } from "react";
import * as Mui from "@mui/material";
import { customAPI } from "services/utils/CustomAPI";
import { showToast } from "services/utils/Status";
import dayjs from "dayjs";
import { rowEndStyle } from "services/constants/Styles";
import { useSelector } from "react-redux";
import { CustomButtonWithBorder } from "components/CustomComponents/CustomButton";
import { ConfirmationDialog } from "components/CustomComponents/ConfirmationDialog";
import { designColors, designRadii } from "services/constants/designTokens";
import { Loader } from "components/Loader";
import {
  Box,
  Collapse,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from "@mui/material";
import {
  ArrowForward,
  DeviceHub,
  Edit,
  ExpandLess,
  ExpandMore,
  Fingerprint,
  KeyboardAltOutlined,
  Smartphone,
} from "@mui/icons-material";

const IN_COLOR = "#059669";
const OUT_COLOR = "#DC2626";

// Section header colours: Used = green, Unused = red (per the mockup).
const SECTION_TONES = {
  used: { text: IN_COLOR, bg: "#ECFDF5", border: "#A7F3D0" },
  unused: { text: OUT_COLOR, bg: "#FEF2F2", border: "#FECACA" },
};

const headCellSx = {
  fontSize: 13,
  fontWeight: 600,
  color: designColors.slate500,
  bgcolor: designColors.slateBg,
  borderBottom: `1px solid ${designColors.slate200}`,
  py: 1.25,
  px: 1.5,
  whiteSpace: "nowrap",
};

const bodyCellSx = {
  fontSize: 14,
  color: designColors.slate800,
  borderBottom: `1px solid ${designColors.slate200}`,
  py: 1.5,
  px: 1.5,
};

const firstFilled = (...values) =>
  values.find((v) => v !== undefined && v !== null && v !== "");

// `edit.edited_at` arrives already formatted ("10-10-2026 13:14"). Passing
// a DD-MM string through dayjs would read it as MM-DD and swap day/month, so
// only ISO-looking values are parsed; anything else is shown as sent.
const formatDateTime = (value) => {
  if (!value) return "";
  if (!/^\d{4}-\d{2}-\d{2}/.test(String(value))) return String(value);
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("DD-MM-YYYY HH:mm") : String(value);
};

// Backend sends `source_type` (MOBILE, ...) plus a ready display `source`
// ("Mobile App"). The icon is picked from whichever of the two is present.
const sourceIcon = (row) => {
  const key = `${row?.source_type || ""} ${row?.source || ""}`.toLowerCase();
  const sx = { fontSize: 20, color: designColors.slate500 };
  if (key.includes("mobile")) return <Smartphone sx={sx} />;
  if (key.includes("bio")) return <Fingerprint sx={sx} />;
  if (key.includes("manual")) return <KeyboardAltOutlined sx={sx} />;
  return <DeviceHub sx={sx} />;
};

const sourceLabel = (row) =>
  firstFilled(row?.source, row?.source_type, row?.device_name) ?? "-";

const DirectionCell = ({ direction }) => {
  if (!direction) return "-";
  const isIn = String(direction).toUpperCase() === "IN";
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <ArrowForward sx={{ fontSize: 20, color: isIn ? IN_COLOR : OUT_COLOR }} />
      <span>{direction}</span>
    </Box>
  );
};

const EditedChip = ({ open, onClick }) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    aria-expanded={open}
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 0.5,
      px: 1.25,
      py: 0.5,
      border: "none",
      cursor: "pointer",
      font: "inherit",
      fontSize: 13,
      fontWeight: 500,
      color: "#C2410C",
      bgcolor: "#FFEDD5",
      borderRadius: designRadii.compactButton,
    }}
  >
    <Edit sx={{ fontSize: 16 }} />
    Edited
  </Box>
);

// "Edited by X • date / Original -> Updated" block under an edited
// row. Backend `edit` shape: { edited_by, edited_at, reason, original_time,
// original_direction, updated_time, updated_direction, updated_label }. The
// extra spellings below are only fallbacks. Reason is not shown: the edit
// screen does not collect one yet, so it is always null.
const EditDetails = ({ row, unused }) => {
  const edit = row?.edit || {};
  const editedBy = firstFilled(
    edit.edited_by_name,
    edit.edited_by,
    edit.modified_by_name,
    edit.modified_by,
    row?.modified_by,
  );
  const editedAt = formatDateTime(
    firstFilled(edit.edited_at, edit.modified_at, edit.created_at),
  );
  const original = edit.original || edit.old || {};
  const updated = edit.updated || edit.new || {};
  const originalTime = firstFilled(
    edit.original_time,
    edit.old_time,
    original.time,
  );
  const originalDirection = firstFilled(
    edit.original_direction,
    edit.old_direction,
    original.direction,
  );
  const explicitUpdatedTime = firstFilled(
    edit.updated_time,
    edit.new_time,
    updated.time,
  );
  const updatedTime = firstFilled(explicitUpdatedTime, row?.time);
  const updatedDirection = firstFilled(
    edit.updated_direction,
    edit.new_direction,
    updated.direction,
    row?.direction,
  );
  // `updated_label` (e.g. "Marked as Unused") replaces the time/direction
  // pair when the backend sends one. An unused punch with neither a label
  // nor a new time was moved out of use, so it gets the same wording.
  const updatedLabel = firstFilled(
    edit.updated_label,
    unused && explicitUpdatedTime === undefined ? "Marked as Unused" : null,
  );
  const markedUnused = Boolean(updatedLabel);
  const hasOriginal = Boolean(originalTime || originalDirection);
  const strong = { fontWeight: 600, color: designColors.slate800 };

  return (
    <Box
      sx={{
        border: `1px solid ${designColors.slate200}`,
        borderRadius: designRadii.card,
        p: 2,
        display: "flex",
        gap: 1.5,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          pt: 0.75,
        }}
      >
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            bgcolor: designColors.slate500,
          }}
        />
        <Box
          sx={{ width: "1px", flexGrow: 1, bgcolor: designColors.slate300 }}
        />
      </Box>
      <Box sx={{ minWidth: 0, flexGrow: 1 }}>
        <Typography sx={{ fontSize: 14, color: designColors.slate500 }}>
          Edited by{" "}
          <Box component="span" sx={strong}>
            {editedBy ?? "-"}
          </Box>
          {editedAt ? <> &nbsp;•&nbsp; {editedAt}</> : null}
        </Typography>
        {hasOriginal || markedUnused ? (
          <Box
            sx={{
              mt: 1.5,
              px: 2,
              py: 1,
              display: "inline-flex",
              flexWrap: "wrap",
              alignItems: "center",
              columnGap: 1.5,
              rowGap: 0.5,
              fontSize: 14,
              color: designColors.slate500,
              bgcolor: designColors.slateBg,
              border: `1px solid ${designColors.slate200}`,
              borderRadius: designRadii.compactButton,
            }}
          >
            <span>Original</span>
            <Box component="span" sx={strong}>
              {firstFilled(originalTime, row?.time) ?? "-"}
            </Box>
            <Box component="span" sx={strong}>
              {firstFilled(originalDirection, row?.direction) ?? ""}
            </Box>
            <ArrowForward sx={{ fontSize: 16 }} />
            <span>Updated</span>
            {markedUnused ? (
              <Box component="span" sx={strong}>
                {updatedLabel}
              </Box>
            ) : (
              <>
                <Box component="span" sx={strong}>
                  {updatedTime ?? "-"}
                </Box>
                <Box component="span" sx={strong}>
                  {updatedDirection ?? ""}
                </Box>
              </>
            )}
          </Box>
        ) : null}
      </Box>
    </Box>
  );
};

// Several unused (void) rows can share one punch_id (each earlier value of
// an edited punch is its own void), so void_id has to win over punch_id.
export const punchRowKey = (row, index) => {
  if (row?._pendingKey) return row._pendingKey;
  if (firstFilled(row?.void_id) !== undefined) return `void-${row.void_id}`;
  if (firstFilled(row?.punch_id) !== undefined) return `punch-${row.punch_id}`;
  return `row-${index}`;
};

// Coloured, collapsible "Used Punches (n)" / "Unused Punches (n)" frame.
export const PunchSectionShell = ({ tone, title, count, children }) => {
  const [open, setOpen] = useState(true);
  const colors = SECTION_TONES[tone];
  return (
    <Box
      sx={{
        border: `1px solid ${designColors.slate200}`,
        borderRadius: designRadii.card,
        overflow: "hidden",
        mb: 2,
      }}
    >
      <Box
        component="button"
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
        sx={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1.5,
          border: "none",
          borderBottom: open ? `1px solid ${colors.border}` : "none",
          bgcolor: colors.bg,
          color: colors.text,
          cursor: "pointer",
          font: "inherit",
        }}
      >
        <Typography sx={{ fontSize: 16, fontWeight: 600, color: "inherit" }}>
          {title} ({count})
        </Typography>
        {open ? <ExpandLess /> : <ExpandMore />}
      </Box>
      <Collapse in={open} unmountOnExit>
        {children}
      </Collapse>
    </Box>
  );
};

// Read-only punch table. `renderAction` adds a trailing Action column (the
// edit screen uses it for the Used/Unused swap); the view tab passes none.
export const PunchSection = ({
  tone,
  title,
  rows,
  recordDate,
  renderAction,
  editsOpenByDefault = true,
}) => {
  // The Edited chip flips a row's details away from the default state.
  const [toggledEdits, setToggledEdits] = useState({});
  const unused = tone === "unused";
  // With the extra Action column (edit screen) the table is one column wider
  // inside the same card, so the cells use tighter side padding to keep
  // "Manual Entry" on one line without forcing a horizontal scroll.
  const cellSx = renderAction ? { ...bodyCellSx, px: 1 } : bodyCellSx;
  const headSx = renderAction ? { ...headCellSx, px: 1 } : headCellSx;
  // Action stays as narrow as its icon, pinned to the right edge, so the
  // spare width is shared by the data columns. Note: in MUI sx a bare
  // `width: 1` means 100%, which is what made this column swallow the row.
  const actionColSx = {
    width: "1%",
    textAlign: "center",
    whiteSpace: "nowrap",
  };
  const headers = [
    "Date",
    "Time",
    "Direction",
    "Source",
    "Global Event ID",
    "Edited",
    ...(renderAction ? ["Action"] : []),
  ];

  return (
    <PunchSectionShell tone={tone} title={title} count={rows.length}>
      <Box sx={{ overflowX: "auto" }}>
        <Table>
          <TableHead>
            <TableRow>
              {headers.map((header) => (
                <TableCell
                  key={header}
                  sx={
                    header === "Action" ? { ...headSx, ...actionColSx } : headSx
                  }
                >
                  {header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={headers.length}
                  sx={{
                    ...cellSx,
                    textAlign: "center",
                    color: designColors.slate600,
                    borderBottom: "none",
                  }}
                >
                  No {title.toLowerCase()}
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row, index) => {
                const key = punchRowKey(row, index);
                const showEdit =
                  Boolean(row?.is_edited) &&
                  editsOpenByDefault !== Boolean(toggledEdits[key]);
                // Every punch shows its own date, so a punch that falls on the
                // next day (night shift) is plain to see.
                const otherDay = row?.date && row.date !== recordDate;
                return (
                  <React.Fragment key={key}>
                    <TableRow>
                      <TableCell
                        sx={{
                          ...cellSx,
                          whiteSpace: "nowrap",
                          ...(otherDay && { fontWeight: 600 }),
                        }}
                      >
                        {row?.date ?? "-"}
                      </TableCell>
                      <TableCell sx={cellSx}>
                        {row?.time ?? "-"}
                        {row?._pendingNote ? (
                          <Typography
                            sx={{
                              fontSize: 12,
                              color: designColors.slate600,
                            }}
                          >
                            {row._pendingNote}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell sx={cellSx}>
                        <DirectionCell direction={row?.direction} />
                      </TableCell>
                      <TableCell sx={cellSx}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          {sourceIcon(row)}
                          <Box component="span" sx={{ whiteSpace: "nowrap" }}>
                            {sourceLabel(row)}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell sx={cellSx}>
                        {firstFilled(
                          row?.global_event_code,
                          row?.global_event_id,
                        ) ?? "-"}
                      </TableCell>
                      <TableCell sx={cellSx}>
                        {row?.is_edited ? (
                          <EditedChip
                            open={showEdit}
                            onClick={() =>
                              setToggledEdits((prev) => ({
                                ...prev,
                                [key]: !prev[key],
                              }))
                            }
                          />
                        ) : (
                          "-"
                        )}
                      </TableCell>
                      {renderAction ? (
                        <TableCell sx={{ ...cellSx, py: 0.5, ...actionColSx }}>
                          {renderAction(row)}
                        </TableCell>
                      ) : null}
                    </TableRow>
                    {showEdit ? (
                      <TableRow>
                        <TableCell
                          colSpan={headers.length}
                          sx={{ ...cellSx, pt: 0.5, pb: 2 }}
                        >
                          <EditDetails row={row} unused={unused} />
                        </TableCell>
                      </TableRow>
                    ) : null}
                  </React.Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </Box>
    </PunchSectionShell>
  );
};

export const PunchEntries = ({
  shiftDetails,
  attendanceDetails,
  setTabValue,
  setEditState,
  setRecallApi,
  loading = false,
}) => {
  const requestCompanyId = useSelector((state) => state.tokenReducer.topbarCompanyId);
  const { domain } = useSelector((state) => state.tokenReducer);
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  // attendance_manual_entry now splits the day's punches into used_punches
  // and unused_punches. Fall back to the legacy flat punch_entries list (all
  // treated as used) if a response ever arrives without the split.
  const usedPunches = Array.isArray(attendanceDetails?.used_punches)
    ? attendanceDetails.used_punches
    : (attendanceDetails?.punch_entries ?? []);
  const unusedPunches = Array.isArray(attendanceDetails?.unused_punches)
    ? attendanceDetails.unused_punches
    : [];

  const makeAbsent = async () => {
    let payload = {
      user: shiftDetails?.user_id,
      date: shiftDetails?.date?.split("-").reverse().join("-"),
      entries: [],
      attendance_record_id: attendanceDetails?.attendance_record_id,
      make_as_absent: true,
    };
    const response = await customAPI(
      `${domain}attendance_manual_entry/?company_id=${requestCompanyId}`,
      "PUT",
      payload,
    );
    if (response.ok) {
      setEditState(true);
      setRecallApi(true);
      showToast("error", response?.responseDetails?.message);
      setTabValue(0);
    }
  };

  const handleCancelClose = () => {
    setShowConfirmationDialog(false);
  };

  // While the record is (re)loading there is nothing real to count yet, so
  // show the loader instead of two empty "No ... punches" sections.
  if (loading) return <Loader />;

  return (
    <>
      <React.Fragment>
        <ConfirmationDialog
          open={showConfirmationDialog}
          onClose={handleCancelClose}
          onConfirm={() => {
            makeAbsent();
          }}
          title={"Mark As Absent"}
          message={`Are you sure you want to mark this day as ${
            attendanceDetails?.mark_as === "Week Off"
              ? "Week Off"
              : attendanceDetails?.mark_as === "Holiday"
                ? "Holiday"
                : "Absent"
          }?`}
          confirmText={"Ok"}
          cancelText="Cancel"
        />
        <Box>
          <PunchSection
            tone="used"
            title="Used Punches"
            rows={usedPunches}
            recordDate={attendanceDetails?.date}
          />
          <PunchSection
            tone="unused"
            title="Unused Punches"
            rows={unusedPunches}
            recordDate={attendanceDetails?.date}
          />

          <Mui.Grid container gap={1} sx={{ ...rowEndStyle, paddingBottom: 2 }}>
            {usedPunches.length + unusedPunches.length > 0 && (
              <CustomButtonWithBorder
                actionFuntion={() => {
                  setShowConfirmationDialog(true);
                }}
                actionName={
                  attendanceDetails?.mark_as === "Week Off"
                    ? "Mark As Week Off"
                    : attendanceDetails?.mark_as === "Holiday"
                      ? "Mark As Holiday"
                      : "Mark As Absent"
                }
                typeName="button"
              />
            )}
          </Mui.Grid>
          {attendanceDetails?.comments ? (
            <Box
              sx={{
                mb: 2,
                px: 2,
                py: 1.5,
                borderRadius: 2,
                backgroundColor: "#fff8db",
                border: "1px solid #f4d35e",
              }}
            >
              <Typography sx={{ fontWeight: 600, mb: 0.5 }}>Info</Typography>
              <Typography variant="body2" color={designColors.slate600}>
                {attendanceDetails?.comments}
              </Typography>
            </Box>
          ) : null}
        </Box>
      </React.Fragment>
    </>
  );
};
